import { FC, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { BrowserRouter as Router } from 'react-router-dom'
import { IntlProvider } from 'react-intl'
/*
 * Bug in material-ui with createMuiTheme, it will be fixed in v5
 * https://github.com/mui-org/material-ui/blob/next/CHANGELOG.md
 * Link issue: https://github.com/mui-org/material-ui/issues/13394
 */
import { ThemeProvider, Theme, StyledEngineProvider, CssBaseline } from '@mui/material'

import Routes from 'common/router/routes'
import { LOCALE } from 'common/api/storageConsts'
import { setApiLanguage } from 'common/api/api'
import { DEFAULT_LOCALE, resolveAppLocale } from 'common/helpers/resolveAppLocale'
import messages from 'common/lang'
import { createExperienceTheme } from 'common/theme/createExperienceTheme'
import ErrorBoundary from 'common/components/ErrorBoundary'
import { ExperienceProvider } from 'common/experience/ExperienceContext'
import SuspenseWrapper from 'common/components/SuspenseWrapper'
import Notifier from 'pages/app/components/Notifier'
import { ApplicationState } from 'store'
import CustomSnackbarProvider from 'common/components/CustomSnackbarProvider'

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

// Any additional component props go here.
interface MainProps {}

// Create an intersection type of the component props and our Redux props.
const Main: FC<MainProps> = () => {
  const { theme } = useSelector((state: ApplicationState) => state.layout)
  const instanceData = useSelector((state: ApplicationState) => state.user.instanceData)
  const locale = resolveAppLocale(instanceData?.language, localStorage.getItem(LOCALE))
  const usedTheme = useMemo(() => createExperienceTheme('legacy', theme), [theme])
  setApiLanguage(locale)
  return (
    <IntlProvider
      messages={messages[locale]}
      locale={locale}
      defaultLocale={DEFAULT_LOCALE}
      onError={() => {}}
    >
      <Router>
        <SuspenseWrapper>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={usedTheme}>
              <ErrorBoundary centerContent>
                <ExperienceProvider>
                  <CustomSnackbarProvider>
                    <CssBaseline />
                    <Notifier />
                    <Routes />
                  </CustomSnackbarProvider>
                </ExperienceProvider>
              </ErrorBoundary>
            </ThemeProvider>
          </StyledEngineProvider>
        </SuspenseWrapper>
      </Router>
    </IntlProvider>
  )
}

// Normally you wouldn't need any generics here (since types infer from the passed functions).
// But since we pass some props from the `index.js` file, we have to include them.
// For an example of a `connect` function without generics, see `./containers/LayoutContainer`.
export default Main
